import type { AccountPeriodRow } from "@/lib/profitability-api";

export interface AccountRangeTotals {
  revenue: number;
  cost: number;
  profit: number;
  /** Null, never 0, when there is no revenue: no revenue has no margin. */
  marginPct: number | null;
  /**
   * Null when the periods carry no hours -- the engine omits them for
   * Aggregate users. Never a sum that silently treats missing as 0.
   */
  hours: number | null;
}

/**
 * The account's revenue, cost, profit, margin and hours over the selected
 * range, summed from the period rows.
 *
 * ONE derivation, used by both the page header and the totals tiles. They were
 * two copies of the same four lines for about an hour, which is exactly how
 * Metryc ended up with four competing definitions of profitability: the header
 * and the tile sat inches apart and nothing but discipline kept them equal.
 *
 * Margin is recomputed from the summed revenue and cost, NOT averaged from the
 * monthly margins -- an average weights a $2k month like a $200k one.
 */
export function accountRangeTotals(periods: AccountPeriodRow[]): AccountRangeTotals {
  let revenue = 0;
  let cost = 0;
  let hours: number | null = 0;
  for (const row of periods) {
    revenue += row.revenue;
    cost += row.cost;
    hours = hours === null || row.hours === undefined ? null : hours + row.hours;
  }
  const profit = revenue - cost;
  return {
    revenue,
    cost,
    profit,
    marginPct: revenue > 0 ? (profit / revenue) * 100 : null,
    hours,
  };
}
